import { DialogDescription } from "@ariakit/react";
import { config } from "../../config";
import { Army } from "../../domain/army";
import { atomic } from "../atomic.css";
import { DialogDismissButton, ShareButton } from "../dialog-buttons";
import { DialogHeader } from "../dialog-header";
import { dialogContent } from "../form-dialog/style.css";
import { ReadonlyField } from "../form-field";
import { useEnsureShareLink } from "../hooks/use-ensure-share-link";
import { Icon } from "../icon";
import { LoadingIndicator } from "../loading-indicator";
import { ModalDialog } from "../modal-dialog";

function QRCode(props: { shareLinkOp: ReturnType<typeof useEnsureShareLink> }) {
  const { shareLinkOp } = props;

  if (shareLinkOp.isPending) {
    return (
      <div
        className={atomic({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          aspectRatio: "square",
          borderRadius: 8,
          backgroundColor: "text-inverse",
        })}
      >
        <LoadingIndicator />
      </div>
    );
  }

  if (shareLinkOp.isFailure) {
    return (
      <div
        className={atomic({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          aspectRatio: "square",
          borderRadius: 8,
          backgroundColor: "text-inverse",
        })}
      >
        <Icon name="cross" />
      </div>
    );
  }

  return (
    <img
      src={`${config.ITC_API_ORIGIN}/qr?${new URLSearchParams({
        url: shareLinkOp.value,
      })}`}
      className={atomic({
        display: "block",
        borderRadius: 8,
        aspectRatio: "square",
        backgroundColor: "text-inverse",
      })}
    />
  );
}

export function ShareArmyDialog({ army }: { army: Army }) {
  const shareLinkOp = useEnsureShareLink(army);

  return (
    <ModalDialog>
      <div className={dialogContent}>
        <DialogHeader heading="Share Army" />

        <div
          className={atomic({
            display: "flex",
            gap: 32,
            flexDirection: "column",
          })}
        >
          <DialogDescription className={atomic({ lineHeight: 1.5 })}>
            Share {army.name} with your friends and enemies! If you make
            changes, you will need to generate a new link for others to see
            them.
          </DialogDescription>

          <div
            className={atomic({
              display: "flex",
              gap: 12,
              flexDirection: "column",
            })}
          >
            <ReadonlyField
              name="name"
              label="Share link"
              value={shareLinkOp.val ?? "Generating unique link..."}
            />
            <ShareButton
              shareData={
                shareLinkOp.isSuccess
                  ? {
                      title: `${army.name} (${army.summary})`,
                      url: shareLinkOp.value,
                    }
                  : null
              }
            />
          </div>

          <div className={atomic({ textAlign: "center" })}>— or —</div>

          <div
            className={atomic({
              borderRadius: 8,
              padding: 32,
              backgroundColor: "background-secondary",
            })}
          >
            <div
              className={atomic({
                maxInlineSize: 240,
                marginInline: "auto",
              })}
            >
              <QRCode shareLinkOp={shareLinkOp} />

              <p
                className={atomic({
                  textAlign: "center",
                  marginBlockStart: 16,
                })}
              >
                Let others scan this QR code with a camera to share in person.
              </p>
            </div>
          </div>
        </div>

        <DialogDismissButton>Done</DialogDismissButton>
      </div>
    </ModalDialog>
  );
}
